import { useCallback, useEffect, useRef, useState } from 'react';
import './CapturePhoto.css';

function stopStream(stream) {
  stream?.getTracks?.().forEach((track) => track.stop());
}

async function openStream(facingMode) {
  return navigator.mediaDevices.getUserMedia({
    video: { facingMode: { ideal: facingMode } },
    audio: false,
  });
}

export default function CapturePhoto({ onCapture, onCancel }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState('environment');
  const [error, setError] = useState('');

  const start = useCallback(async (nextFacing) => {
    stopStream(streamRef.current);
    streamRef.current = null;
    setError('');
    try {
      const stream = await openStream(nextFacing);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      setError(err?.message || 'Camera is not available on this device.');
    }
  }, []);

  useEffect(() => {
    start(facing);
    return () => stopStream(streamRef.current);
  }, [facing, start]);

  const takePhoto = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.86));
    if (!blob) return;
    stopStream(streamRef.current);
    onCapture(new File([blob], `progress-${Date.now()}.jpg`, { type: 'image/jpeg' }));
  };

  return (
    <div className="fp-photo-overlay" role="dialog" aria-label="Take photo">
      <video ref={videoRef} className="fp-photo-video" playsInline muted autoPlay />
      {error ? <p className="fp-photo-error">{error}</p> : null}
      <div className="fp-photo-bar">
        <button type="button" className="fp-photo-cancel" onClick={onCancel}>Cancel</button>
        <button type="button" className="fp-photo-shutter" onClick={takePhoto} disabled={Boolean(error)} aria-label="Take photo" />
        <button type="button" className="fp-photo-flip" onClick={() => setFacing((prev) => (prev === 'environment' ? 'user' : 'environment'))}>
          Flip
        </button>
      </div>
    </div>
  );
}
